import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Gallery = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/projects`)
      .then((r) => r.json())
      .then((data) => {
        setProjects(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);
  const categories = ["All", ...new Set(projects.map((p) => p.category))];
  const [tall, setTall] = useState({});

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 lg:py-32 bg-espresso-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <p className="section-label" data-testid="gallery-label">Selected Work</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-cream mt-4" data-testid="gallery-heading">
              Recent commissions from <span className="italic text-brass">the workshop</span>
            </h2>
          </motion.div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs tracking-[0.15em] uppercase px-4 py-2 border transition-colors ${
                  filter === c
                    ? "border-brass bg-brass text-espresso-900 font-semibold"
                    : "border-espresso-600 text-cream-muted hover:border-brass/60 hover:text-brass"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-16 text-cream-muted text-sm" data-testid="gallery-loading">
            Loading projects…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 items-start" data-testid="gallery-grid">
            {visible.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden border border-espresso-600 bg-espresso-900 ${
                  i % 5 === 0 && !tall[p.id] ? "md:col-span-2" : ""
                }`}
              >
                <div className={`overflow-hidden ${tall[p.id] ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
                  <img
                    src={p.image_url}
                    alt={p.title}
                    loading="lazy"
                    onLoad={(e) => {
                      const { naturalWidth: w, naturalHeight: h } = e.target;
                      if (h > w && !tall[p.id]) setTall((t) => ({ ...t, [p.id]: true }));
                    }}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 inset-x-0 p-6 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-brass">{p.category}</p>
                  <h3 className="font-serif text-xl text-cream mt-1">{p.title}</h3>
                  <p className="text-xs text-cream-muted mt-2 max-w-md">{p.description}</p>
                </div>
              </motion.article>
            ))}
            {visible.length === 0 && (
              <p className="text-cream-muted text-sm col-span-full" data-testid="gallery-empty">
                No projects in this category yet.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
